import { Toast } from '@fattureincloud/fic-design-system'
import React, { FC, useEffect, useState } from 'react'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'

import { httpRequest } from '../../common/network'
import { DraggableContent } from '../../layout_components/PageSection'
import { errorMessage, locale } from '../../locales/locale'
import { ArticleInfo, SectionColumnInfo, SectionInfo } from '../definitions'
import EditorModeToggle from '../EditorMode/EditorModeToggle'
import SectionEditButtons from '../Sections/SectionEditButtons'
import { SectionType } from './dnd.components'
import SubsectionDropContainer from './SubsectionDropContainer'

interface Props {
  section: SectionInfo
  subsections: SectionInfo[]
  articles: ArticleInfo[]
  parent_section: number
  updateSection: (section: SectionInfo, type: SectionType) => void
  reloadSection: () => void
  changeView: (permalink: string, type: string) => void
}

const SectionEditable: FC<Props> = (props: Props) => {
  const [subsections, setSubsections] = useState<SectionInfo[]>(
    props.subsections
  )
  const [sectionBoxes, setSectionBoxes] = useState<SectionColumnInfo[]>([])

  useEffect(() => {
    const sectionBoxes = getColumns()
    setSectionBoxes(sectionBoxes)
  }, [props.section, subsections, props.articles])

  const getColumns = () => {
    const sectionBoxes: SectionColumnInfo[] = []
    sectionBoxes.push({
      //aggiungo macro sezione
      ...props.section,
      id: String(props.section.id),
      articles: props.articles,
      type: SectionType.Main,
    })
    subsections.forEach((s) => {
      // aggiungo sezioni
      sectionBoxes.push({
        ...s,
        id: String(s.id),
        articles: s.articles,
        type: SectionType.Subsection,
      })
      s.subSections.forEach((sub) => {
        // aggiungo sottosezioni
        sectionBoxes.push({
          ...sub,
          id: String(sub.id),
          articles: sub.articles,
          type: SectionType.SubSubSection,
        })
        sub.subSubSections.forEach((subsub) => {
          // aggiungo sotto sotto sezioni
          sectionBoxes.push({
            ...subsub,
            id: String(subsub.id),
            articles: subsub.articles,
            type: SectionType.SubSubSubSection,
          })
        })
      })
    })

    return sectionBoxes
  }

  const onDragEnd = (result: DropResult) => {
    // dropped inside of the list
    const { destination, source } = result
    if (source && destination) {
      setSectionBoxes((prevState) => {
        const { droppableId: sourceId, index: sourceIndex } = source
        const { droppableId: destinationId, index: destinationIndex } =
          destination
        const sourceContainer = prevState.find(
          (column) => column.id === sourceId
        )
        const destinationContainer = prevState.find(
          (column) => column.id === destinationId
        )
        const sourceArticles = Array.from(sourceContainer.articles)
        const destinationArticles = Array.from(destinationContainer.articles)
        const isSameContainer = sourceContainer.id === destinationContainer.id

        const movedArticle = sourceArticles.splice(sourceIndex, 1)[0]
        if (isSameContainer) {
          sourceArticles.splice(destinationIndex, 0, movedArticle)
        } else {
          destinationArticles.splice(destinationIndex, 0, movedArticle)
        }
        const newSourceContainer = {
          ...sourceContainer,
          articles: sourceArticles,
        }
        const newDestinationContainer = {
          ...destinationContainer,
          articles: destinationArticles,
        }
        const sections = prevState.map((column) => {
          if (column.id === newSourceContainer.id) {
            return newSourceContainer
          } else if (
            column.id === newDestinationContainer.id &&
            !isSameContainer
          ) {
            return newDestinationContainer
          } else {
            return column
          }
        })
        return [...sections]
      })
    }
  }

  const saveOrder = () => {
    httpRequest({
      method: 'POST',
      path: `help/sections/${props.parent_section}/order`,
      data: {
        sections: sectionBoxes,
      },
      onSuccess: () => {
        props.reloadSection()
        Toast.success(locale('Ordine salvato'))
      },
      onError: (res) => {
        Toast.error(errorMessage, { content: res.message })
      },
    })
  }

  const replace = (
    selectedSection: SectionInfo,
    sectionArray: SectionInfo[],
    sort: 1 | 0 // => 1 up, 0 down
  ) => {
    const subsectionIndex = sectionArray.findIndex(
      (s) => Number(s.id) === Number(selectedSection.id)
    )
    const replaceIdx = sort === 1 ? subsectionIndex - 1 : subsectionIndex + 1
    if (replaceIdx < 0 || replaceIdx >= sectionArray.length) return sectionArray
    const replacedSubsection = sectionArray[replaceIdx]
    const subsectionCopy = [...sectionArray]
    subsectionCopy.splice(replaceIdx, 1, selectedSection)
    subsectionCopy.splice(subsectionIndex, 1, replacedSubsection)
    return subsectionCopy
  }

  const moveSection = (selectedIdx: number, sort: 1 | 0) => {
    // sort 1=> up, 0=> down
    const selectedSection = sectionBoxes[selectedIdx]
    if (selectedSection.type === SectionType.Subsection) {
      const res = replace(selectedSection, subsections, sort)
      setSubsections(res)
    }
    if (selectedSection.type === SectionType.SubSubSection) {
      const parentSectionIdx = subsections.findIndex(
        (s) => s.id === Number(selectedSection.parent_section)
      )
      const parentSection = subsections[parentSectionIdx]
      const orderesSubsections = replace(
        selectedSection,
        parentSection.subSections,
        sort
      )
      const subsectionCopy = [...subsections]
      subsectionCopy.splice(parentSectionIdx, 1, {
        ...parentSection,
        subSections: orderesSubsections,
      })
      setSubsections(subsectionCopy)
    }
    if (selectedSection.type === SectionType.SubSubSubSection) {
      let sectionIdx = -1
      let subSectionIdx = -1
      subsections.forEach((s, sIdx) => {
        s.subSections.forEach((sub, idx) => {
          if (sub.id === selectedSection.parent_section) {
            subSectionIdx = idx
            sectionIdx = sIdx
          }
        })
      })

      const orderedSubSubsections = replace(
        selectedSection,
        subsections[sectionIdx].subSections[subSectionIdx].subSubSections,
        sort
      )
      const subsectionCopy = [...subsections]
      subsectionCopy[sectionIdx].subSections[subSectionIdx] = {
        ...subsections[sectionIdx].subSections[subSectionIdx],
        subSubSections: orderedSubSubsections,
      }
      setSubsections(subsectionCopy)
    }
  }

  return (
    <>
      <EditorModeToggle />
      <SectionEditButtons
        saveOrder={saveOrder}
        reloadSection={props.reloadSection}
        parent_section={props.parent_section}
      />
      <DraggableContent>
        <DragDropContext onDragEnd={onDragEnd}>
          {sectionBoxes.map((section, idx) => (
            <SubsectionDropContainer
              key={section.id}
              section={section}
              reloadSection={props.reloadSection}
              changeView={props.changeView}
              moveUp={() => moveSection(idx, 1)}
              moveDown={() => moveSection(idx, 0)}
            />
          ))}
        </DragDropContext>
      </DraggableContent>
    </>
  )
}

export default SectionEditable
